import { Link } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import SectionHeading from "@/components/feature/SectionHeading";
import AnimeCard from "@/components/feature/AnimeCard";
import NewsCard from "@/components/feature/NewsCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { animeCategories, animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import { filterAnime, formatNumber } from "@/utils/content";

export default function Home() {
  useDocumentMeta(
    "动漫天堂-海量动漫资源精彩推荐与介绍",
    "动漫天堂为您提供丰富的国内外动漫资源，涵盖最新动漫资讯和精彩剧情介绍，满足动漫爱好者的观影与了解需求。",
    "动漫天堂,动漫资源,动漫推荐,动漫资讯,动漫剧情,动画片,动漫观看,动漫下载",
    "/"
  );

  const latest = filterAnime(animeList, { sort: "update" }).slice(0, 8);
  const popular = filterAnime(animeList, { sort: "popular" }).slice(0, 6);
  const featuredNews = newsList.slice(0, 3);
  const totalPopularity = animeList.reduce((sum, item) => sum + item.popularity, 0);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteNavbar />

      <main className="flex-1">
        <HeroCarousel />

        <section className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-page px-4 py-12 md:px-6 md:py-16">
            <SectionHeading
              eyebrow="Type Explorer"
              title="按类型快速进入"
              description="从热血到治愈，八大主流类型一键直达分类页，筛选条件会自动带入。"
              moreTo="/category"
              moreLabel="全部分类"
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
              {animeCategories.map((category) => {
                const count = animeList.filter((item) => item.tags.includes(category.name)).length;
                return (
                  <Link
                    key={category.name}
                    to={`/category?type=${encodeURIComponent(category.name)}`}
                    className="group flex cursor-pointer flex-col items-center gap-3 rounded-lg border border-background-200/80 bg-background-50 px-2 py-5 transition-colors hover:border-primary-300 hover:bg-primary-50/60"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-2xl text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                      <i className={category.icon} />
                    </span>
                    <span className="text-center">
                      <span className="block text-sm font-semibold text-foreground-900">
                        {category.name}
                      </span>
                      <span className="mt-1 block text-[11px] text-foreground-400">
                        {count} 部作品
                      </span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100/70">
          <div className="mx-auto w-full max-w-page px-4 py-12 md:px-6 md:py-16">
            <SectionHeading
              eyebrow="Just Updated"
              title="最新动漫更新"
              description="按更新时间排序，紧跟每一部作品的更新节奏。"
              moreTo="/category?sort=update"
              moreLabel="查看更新列表"
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {latest.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-primary-50/40">
          <div className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-12">
            <div className="grid grid-cols-1 gap-6 rounded-lg border border-background-200/80 bg-background-50 p-6 md:grid-cols-[1.2fr_2fr] md:items-center md:p-8">
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-primary-600">
                  数据速览
                </span>
                <h2 className="mt-3 font-heading text-xl font-extrabold leading-8 text-foreground-950 md:text-2xl">
                  一个专注「先了解，再决定」的动漫内容站
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-600">
                  我们不做播放，也不做下载。这里只有作品资料、剧情介绍与资讯整理，让你在点开任何一集之前，先知道它讲的是什么。
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: String(animeList.length), label: "收录作品", suffix: "部" },
                  { value: String(newsList.length), label: "资讯文章", suffix: "篇" },
                  { value: formatNumber(totalPopularity), label: "站内热度", suffix: "" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-lg bg-background-100 px-3 py-5 text-center">
                    <p className="font-heading text-2xl font-extrabold text-primary-600 md:text-3xl">
                      {stat.value}
                      <span className="ml-0.5 text-xs font-medium text-foreground-500">{stat.suffix}</span>
                    </p>
                    <p className="mt-2 text-xs text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-page px-4 pb-12 md:px-6 md:pb-16">
            <SectionHeading
              eyebrow="Most Popular"
              title="热门动漫推荐"
              description="根据站内浏览热度与编辑推荐综合排序，排名每周更新。"
              moreTo="/category?sort=popular"
              moreLabel="热门榜单"
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5">
              {popular.map((anime, index) => (
                <AnimeCard key={anime.id} anime={anime} rank={index + 1} showPopularity />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100/70">
          <div className="mx-auto w-full max-w-page px-4 py-12 md:px-6 md:py-16">
            <SectionHeading
              eyebrow="Newsroom"
              title="动漫资讯精选"
              description="新番动态、制作访谈与剧情解析，帮你跟上本周的讨论。"
              moreTo="/news"
              moreLabel="全部资讯"
            />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
              {featuredNews.map((news) => (
                <NewsCard key={news.id} news={news} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-page px-4 py-12 md:px-6 md:py-16">
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-12">
              <div className="relative h-[280px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[360px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20anime%20style%20illustration%20of%20a%20cozy%20two%20story%20bookstore%20on%20a%20quiet%20street%20at%20dusk%2C%20glowing%20orange%20windows%2C%20movie%20posters%20and%20shelves%20visible%20inside%2C%20deep%20amber%20evening%20sky%2C%20clean%20detailed%20background%20art%20with%20welcoming%20atmosphere&width=1200&height=800&seq=anime-about-01&orientation=landscape"
                  alt="动漫天堂内容站氛围示意"
                  title="动漫天堂 动漫内容聚合"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-primary-600">
                  About Us
                </span>
                <h2 className="mt-3 font-heading text-2xl font-extrabold leading-9 text-foreground-950 md:text-3xl">
                  认真整理每一部作品的剧情与来龙去脉
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
                  动漫天堂是一个由动漫爱好者维护的内容站。我们把作品资料、剧情梗概与站内资讯放在同一条路径上，让你从一个页面就能看清一部作品的类型、制作阵容与故事走向。
                </p>
                <ul className="mt-5 space-y-3 text-sm text-foreground-700">
                  {[
                    "覆盖日本、中国、美国等地区的动漫作品",
                    "按类型、地区、年份多维筛选，支持多选",
                    "每部作品都配有剧情介绍与关联资讯",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-100 text-[11px] text-accent-700">
                        <i className="ri-check-line" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/about"
                  className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  了解动漫天堂
                  <i className="ri-arrow-right-line text-base" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}